import EventEmitter from "events";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Room, RoomEvent } from "livekit-client";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import { logLiveKit, LogLevel } from "utils/sentryLogger";
import useLiveKitConnectionLogger from "hooks/livekit/useLiveKitConnectionLogger/useLiveKitConnectionLogger";

export default function useRoom(localTracks, onError, connectionOptions, roomOptions, { sessionId, userId, groupCode } = {}) {
  const [room, setRoom] = useState(new EventEmitter());
  const [isConnecting, setIsConnecting] = useState(false);
  const { isRecordSessions, isSpectator } = useAppStateContext();
  const optionsRef = useRef(connectionOptions);
  const isMountedRef = useRef(true);

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize connection logger
  const {
    logConnectionInitiated,
    logConnectionSuccess,
    logConnectionFailure,
    flushConnectionEventsSummary,
  } = useLiveKitConnectionLogger(room, { sessionId, userId, groupCode });

  useEffect(() => {
    // This allows the connect function to always access the most recent version of the connectionOptions object.
    // This allows us to reliably use the connect function at any time.
    optionsRef.current = connectionOptions;
  }, [connectionOptions]);

  const connect = useCallback(
    (url, token, context = {}) => {
      setIsConnecting(true);
      logConnectionInitiated(url, context);
      const room = new Room(roomOptions);

      return Sentry.startSpan(
        {
          name: "livekit.room.connect",
          op: "webrtc.connect",
          attributes: {
            "livekit.room_url": url,
            "session.id": context.sessionId || "",
            "session.group_code": context.groupCode || "",
          },
        },
        async () => {
          try {
            await room.connect(url, token, { ...optionsRef.current });

            // Prevent state updates if component unmounted during connection
            if (!isMountedRef.current) {
              room.disconnect();
              return;
            }

            logConnectionSuccess(localTracks, context);
            if (isRecordSessions && !isSpectator) {
              // WebKit has no getDisplayMedia, so LiveKit throws DeviceUnsupportedError
              // here on every iPad join. The session is unaffected (the call isn't
              // awaited), so log a warning rather than let it surface as an unhandled
              // rejection.
              room.localParticipant.setScreenShareEnabled(
                true,
                {
                  selfBrowserSurface: "include",
                  surfaceSwitching: "exclude",
                  preferCurrentTab: true,
                }
              ).catch((e) => {
                logLiveKit("Screen share for session recording failed to start", {
                  level: LogLevel.WARNING,
                  event: "record_screen_share_failed",
                  sessionId: context.sessionId,
                  errorName: e?.name,
                  errorMessage: e?.message,
                  roomState: room?.state,
                });
              });
            }
            await Promise.all(localTracks.map((track) => room.localParticipant.publishTrack(track)));

            if (!isMountedRef.current) {
              room.disconnect();
              return;
            }

            setRoom(room);
            const disconnect = () => room.disconnect();
            room.once(RoomEvent.Disconnected, () => {
              // Reset the room only after all other `disconnected` listeners have been called.
              setTimeout(() => {
                if (isMountedRef.current) {
                  setRoom(new EventEmitter());
                }
              });
              window.removeEventListener("beforeunload", disconnect);
              window.removeEventListener("pagehide", disconnect);
            });
            setIsConnecting(false);
            // Add a listener to disconnect from the room when a user closes their browser.
            // pagehide is the reliable one on iOS/iPadOS, where beforeunload often never
            // fires, and it is the recommended replacement everywhere else.
            window.addEventListener("beforeunload", disconnect);
            window.addEventListener("pagehide", disconnect);
          } catch (error) {
            logConnectionFailure(error);
            onError(error);
            if (isMountedRef.current) {
              setIsConnecting(false);
            }
            throw error;
          }
        }
      );
    },
    [localTracks, onError, logConnectionInitiated, logConnectionSuccess, logConnectionFailure, roomOptions, isRecordSessions, isSpectator]
  );

  return { room, isConnecting, connect, flushConnectionEventsSummary };
}
